import { type BlockedVariable, PLAN_RANK } from "./plan-rules";

/** Error code dotell-backend returns (HTTP 403) when a version create/update
 * is refused because the story uses variables above the account's plan. */
export const VARIABLES_PLAN_REQUIRED = "VARIABLES_PLAN_REQUIRED";

/** Thrown by the version hooks when publishing is refused for plan reasons —
 * locally (pre-flight) or by the backend. Callers skip their generic error
 * toast for it; the upgrade dialog has already been opened. */
export class VariablesPlanRequiredError extends Error {
  readonly blocked: BlockedVariable[];

  constructor(blocked: BlockedVariable[]) {
    super(VARIABLES_PLAN_REQUIRED);
    this.name = "VariablesPlanRequiredError";
    this.blocked = blocked;
  }
}

type ErrorBody = { code?: string; data?: unknown };

/** Reads the blocked-variables payload out of an RTK Query error (`.data`) or
 * an axios error (`.response.data`). Returns null when the error is something
 * else — or when a VARIABLES_PLAN_REQUIRED body carries no usable variable
 * list, so a malformed refusal falls through to the generic error path
 * instead of opening an empty upgrade dialog. */
export const blockedVariablesFromApiError = (
  error: unknown,
): BlockedVariable[] | null => {
  const err = error as
    | { data?: ErrorBody; response?: { data?: ErrorBody } }
    | null
    | undefined;
  const data = err?.response?.data ?? err?.data;
  if (!data || data.code !== VARIABLES_PLAN_REQUIRED) return null;
  const variables = (data.data as { variables?: unknown } | undefined)
    ?.variables;
  if (!Array.isArray(variables)) return null;
  const valid = variables.filter(
    (v): v is BlockedVariable =>
      !!v &&
      typeof (v as BlockedVariable).name === "string" &&
      typeof (v as BlockedVariable).requiredPlan === "string" &&
      // Only known tiers: the dialog's labels/ranks are keyed by them.
      (v as BlockedVariable).requiredPlan in PLAN_RANK,
  );
  return valid.length > 0 ? valid : null;
};
